/**
 * Utility functions for KATA Board v2
 * File handling, formatting, validation, and helper functions
 */

import type {
  KATABoardV2,
  PDCACycleV2,
  FileAttachment,
  IndicatorDataPoint,
  Outcome,
  BenefitMeasurement
} from '../types/kataBoardV2';

// File handling utilities
export const fileUtils = {
  // Format file size for display
  formatFileSize: (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Check if file is an image
  isImageFile: (mimeType: string): boolean => {
    return mimeType.startsWith('image/');
  },

  // Check if file is a PDF
  isPdfFile: (mimeType: string): boolean => {
    return mimeType === 'application/pdf';
  },

  // Check if file is supported
  isSupportedFile: (mimeType: string): boolean => {
    const supportedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    return supportedTypes.includes(mimeType);
  },

  // Get file icon based on mime type
  getFileIcon: (mimeType: string): string => {
    if (fileUtils.isImageFile(mimeType)) return '🖼️';
    if (fileUtils.isPdfFile(mimeType)) return '📄';
    if (mimeType.includes('word')) return '📝';
    if (mimeType.includes('text')) return '📄';
    return '📎';
  },

  // Validate file upload
  validateFile: (file: File, maxSize = 10 * 1024 * 1024): { valid: boolean; error?: string } => {
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size must be less than ${fileUtils.formatFileSize(maxSize)}`
      };
    }

    if (!fileUtils.isSupportedFile(file.type)) {
      return {
        valid: false,
        error: 'File type not supported. Please upload images, PDFs, or documents.'
      };
    }

    return { valid: true };
  },

  // Create file preview URL
  createPreviewUrl: (file: File): string => {
    return URL.createObjectURL(file);
  },

  // Clean up preview URL
  revokePreviewUrl: (url: string): void => {
    URL.revokeObjectURL(url);
  }
};

// Date and time utilities
export const dateUtils = {
  // Format date for display
  formatDate: (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  // Format datetime for display
  formatDateTime: (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  // Format date for input field
  formatDateForInput: (dateString: string): string => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  },

  // Check if date is overdue
  isOverdue: (dateString: string): boolean => {
    const date = new Date(dateString);
    const now = new Date();
    return date < now;
  },

  // Get relative time (e.g., "2 days ago")
  getRelativeTime: (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    return `${Math.floor(diffInSeconds / 31536000)} years ago`;
  },

  // Get days until date
  getDaysUntil: (dateString: string): number => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = date.getTime() - now.getTime();
    return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  }
};

// Styling utilities
export const styleUtils = {
  // Get priority color classes
  getPriorityColor: (priority: string): string => {
    const colors = {
      high: 'text-red-600 bg-red-100 border-red-200',
      medium: 'text-yellow-600 bg-yellow-100 border-yellow-200',
      low: 'text-green-600 bg-green-100 border-green-200'
    };
    return colors[priority as keyof typeof colors] || colors.medium;
  },

  // Get status color classes
  getStatusColor: (status: string): string => {
    const colors = {
      assigned: 'text-blue-600 bg-blue-100 border-blue-200',
      in_progress: 'text-yellow-600 bg-yellow-100 border-yellow-200',
      completed: 'text-green-600 bg-green-100 border-green-200',
      overdue: 'text-red-600 bg-red-100 border-red-200',
      cancelled: 'text-gray-600 bg-gray-100 border-gray-200'
    };
    return colors[status as keyof typeof colors] || colors.assigned;
  },

  // Get impact level color classes
  getImpactColor: (impact: string): string => {
    const colors = {
      high: 'text-red-600 bg-red-100 border-red-200',
      medium: 'text-yellow-600 bg-yellow-100 border-yellow-200',
      low: 'text-green-600 bg-green-100 border-green-200'
    };
    return colors[impact as keyof typeof colors] || colors.medium;
  },

  // Get progress bar color
  getProgressColor: (percentage: number): string => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  }
};

// Validation utilities
export const validationUtils = {
  // Validate email
  isValidEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  // Validate required field
  isRequired: (value: any): boolean => {
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number') return !isNaN(value);
    return value != null;
  },

  // Validate numeric value
  isValidNumber: (value: string, min?: number, max?: number): boolean => {
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    if (min !== undefined && num < min) return false;
    if (max !== undefined && num > max) return false;
    return true;
  },

  // Validate date
  isValidDate: (dateString: string): boolean => {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  },

  // Validate SMART objective
  validateSmartObjective: (objective: string): { valid: boolean; suggestions?: string[] } => {
    const suggestions: string[] = [];
    
    if (objective.length < 10) {
      suggestions.push('Objective should be more detailed');
    }
    
    // Check for measurable elements
    const hasNumbers = /\d/.test(objective);
    if (!hasNumbers) {
      suggestions.push('Include specific numbers or metrics');
    }
    
    // Check for time element
    const hasTimeWords = /\b(by|until|within|before|after|in)\b/i.test(objective);
    if (!hasTimeWords) {
      suggestions.push('Include a specific timeframe');
    }
    
    return {
      valid: suggestions.length === 0,
      suggestions: suggestions.length > 0 ? suggestions : undefined
    };
  }
};

// Calculation utilities
export const calculationUtils = {
  // Calculate progress percentage
  calculateProgress: (completed: number, total: number): number => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  },

  // Calculate average
  calculateAverage: (values: number[]): number => {
    if (values.length === 0) return 0;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  },

  // Calculate trend (positive/negative/neutral)
  calculateTrend: (values: number[]): 'up' | 'down' | 'neutral' => {
    if (values.length < 2) return 'neutral';
    
    const first = values[0];
    const last = values[values.length - 1];
    const threshold = 0.05; // 5% change threshold
    
    const change = (last - first) / first;
    
    if (change > threshold) return 'up';
    if (change < -threshold) return 'down';
    return 'neutral';
  },

  // Calculate financial impact
  calculateFinancialImpact: (benefits: BenefitMeasurement[]): number => {
    return benefits.reduce((total, benefit) => {
      return total + (benefit.financial_impact || 0);
    }, 0);
  },

  // Calculate ROI
  calculateROI: (benefits: number, investment: number): number => {
    if (investment === 0) return 0;
    return ((benefits - investment) / investment) * 100;
  }
};

// Chart utilities
export const chartUtils = {
  // Prepare chart data for visualization
  prepareChartData: (dataPoints: IndicatorDataPoint[], outcome: Outcome) => {
    const sortedPoints = [...dataPoints]
      .filter(point => point.date != null) // Filter out null/undefined dates
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateA - dateB;
      });

    return {
      labels: sortedPoints.map(point => point.date ? dateUtils.formatDate(point.date) : ''),
      datasets: [
        {
          label: outcome.name,
          data: sortedPoints.map(point => point.value),
          borderColor: outcome.kpi_type === 'increase' ? '#10b981' : '#ef4444',
          backgroundColor: outcome.kpi_type === 'increase' ? '#10b98120' : '#ef444420',
          tension: 0.1
        },
        ...(outcome.target_value ? [{
          label: 'Target',
          data: Array(sortedPoints.length).fill(outcome.target_value),
          borderColor: '#6b7280',
          borderDash: [5, 5],
          pointRadius: 0
        }] : [])
      ]
    };
  },

  // Get chart color based on performance
  getChartColor: (value: number, target: number, kpiType: 'increase' | 'decrease'): string => {
    const isOnTarget = kpiType === 'increase' ? value >= target : value <= target;
    return isOnTarget ? '#10b981' : '#ef4444';
  }
};

// Text utilities
export const textUtils = {
  // Truncate text with ellipsis
  truncate: (text: string, length: number): string => {
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
  },

  // Convert to title case
  toTitleCase: (text: string): string => {
    return text.replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  },

  // Convert camelCase or snake_case to readable text
  humanize: (text: string): string => {
    return text
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()) // Capitalize first letter of each word
      .trim();
  },

  // Extract initials from name
  getInitials: (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);
  },

  // Generate slug from text
  slugify: (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }
};

// Search and filter utilities
export const searchUtils = {
  // Simple text search
  searchText: (text: string, query: string): boolean => {
    return text.toLowerCase().includes(query.toLowerCase());
  },

  // Search in multiple fields
  searchMultipleFields: (item: any, query: string, fields: string[]): boolean => {
    return fields.some(field => {
      const value = field.split('.').reduce((obj, key) => obj?.[key], item);
      return value && searchUtils.searchText(String(value), query);
    });
  },

  // Filter PDCA cycles
  filterPdcaCycles: (cycles: PDCACycleV2[], filters: any): PDCACycleV2[] => {
    return cycles.filter(cycle => {
      if (filters.priority && cycle.priority !== filters.priority) return false;
      if (filters.status && cycle.status !== filters.status) return false;
      if (filters.responsible_lead && cycle.responsible_lead !== filters.responsible_lead) return false;
      if (filters.overdue_only && !cycle.is_overdue) return false;
      if (filters.search && !searchUtils.searchMultipleFields(cycle, filters.search, 
        ['action_required', 'root_cause', 'comments'])) return false;
      return true;
    });
  },

  // Filter file attachments
  filterFileAttachments: (files: FileAttachment[], filters: any): FileAttachment[] => {
    return files.filter(file => {
      if (filters.attachment_type && file.attachment_type !== filters.attachment_type) return false;
      if (filters.is_before_image !== undefined && file.is_before_image !== filters.is_before_image) return false;
      if (filters.is_after_image !== undefined && file.is_after_image !== filters.is_after_image) return false;
      if (filters.search && !searchUtils.searchMultipleFields(file, filters.search, 
        ['original_filename', 'title', 'description'])) return false;
      return true;
    });
  }
};

// Export utilities
export const exportUtils = {
  // Export data to CSV
  exportToCSV: (data: any[], filename: string): void => {
    if (data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          return typeof value === 'string' && value.includes(',') 
            ? `"${value}"` 
            : value;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  },

  // Export data to JSON
  exportToJSON: (data: any, filename: string): void => {
    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }
};

// Notification utilities
export const notificationUtils = {
  // Get notification icon based on type
  getNotificationIcon: (type: string): string => {
    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️'
    };
    return icons[type as keyof typeof icons] || icons.info;
  },

  // Get notification color classes
  getNotificationColor: (type: string): string => {
    const colors = {
      success: 'bg-green-50 border-green-200 text-green-800',
      error: 'bg-red-50 border-red-200 text-red-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      info: 'bg-blue-50 border-blue-200 text-blue-800'
    };
    return colors[type as keyof typeof colors] || colors.info;
  }
};

// Component navigation utilities
export const navigationUtils = {
  // Get component names
  getComponentName: (componentNumber: number): string => {
    const names = {
      1: 'Project Charter',
      2: 'Current/Target Condition',
      3: 'Process Map',
      4: 'Process Observations',
      5: 'PDCA Cycles',
      6: 'Indicator Trend',
      7: 'Before / After Status',
      8: 'Benefit Analysis'
    };
    return names[componentNumber as keyof typeof names] || `Component ${componentNumber}`;
  },

  // Get component route
  getComponentRoute: (workshopId: number, componentNumber: number): string => {
    return `/workshops/${workshopId}/kata-board-v2?component=${componentNumber}`;
  },

  // Check if component is complete
  isComponentComplete: (kataBoard: KATABoardV2, componentNumber: number): boolean => {
    if (!kataBoard) return false;
    
    switch (componentNumber) {
      case 1: return !!(
        kataBoard.smart_objective?.trim() && 
        (kataBoard.team_description || kataBoard.workshop_objective_title)
      );
      case 2: return (kataBoard.outcomes || []).length > 0;
      case 3: return (kataBoard.outcomes || []).length > 0;
      case 4: return (
        (kataBoard.sipoc_suppliers || []).length > 0 ||
        (kataBoard.sipoc_inputs || []).length > 0 ||
        (kataBoard.sipoc_processes || []).length > 0 ||
        (kataBoard.sipoc_outputs || []).length > 0 ||
        (kataBoard.sipoc_customers || []).length > 0 ||
        (kataBoard.file_attachments || []).some(f => f.attachment_type === 'process_map')
      );
      case 5: {
        // Component 5 is complete if there are process observations OR file attachments for observations
        const hasObservations = kataBoard.process_observations && kataBoard.process_observations.length > 0;
        const hasObservationFiles = (kataBoard.file_attachments || []).some(f => f.attachment_type === 'observation_file');
        return hasObservations || hasObservationFiles;
      }
      case 6: return (kataBoard.pdca_cycles || []).length > 0;
      case 7: return (kataBoard.outcomes || []).some(o => (o.factors || []).length > 0);
      case 8: return (kataBoard.file_attachments || []).some(f => f.is_before_image);
      case 10: return (kataBoard.benefit_measurements || []).length > 0;
      default: return false;
    }
  },

  // Get completion percentage
  getCompletionPercentage: (kataBoard: KATABoardV2): number => {
    if (!kataBoard) return 0;
    
    const availableComponents = [1, 2, 3, 4, 5, 6, 7, 8]; // Now 8 components total
    const completedComponents = availableComponents
      .filter(componentNumber => navigationUtils.isComponentComplete(kataBoard, componentNumber))
      .length;
    
    return calculationUtils.calculateProgress(completedComponents, availableComponents.length);
  }
};

// Constants and mappings
export const constants = {
  SECTION_TYPES: {
    problem_statement: 'Problem Statement/Current Condition',
    target_condition: 'Target Condition',
    process_map: 'Process Map/Layout',
    process_observations: 'Process Observations'
  },

  ATTACHMENT_TYPES: {
    workshop_image: 'Workshop Image',
    process_map: 'Process Map/Layout',
    observation_file: 'Process Observation File',
    before_image: 'Before Status Image',
    after_image: 'After Status Image',
    pdca_file: 'PDCA Cycle File'
  },

  BENEFIT_TYPES: {
    time_saved: 'Time Saved',
    space_optimized: 'Space Optimized',
    energy_reduction: 'Energy Reduction',
    other_improvement: 'Other Improvement',
    investment_required: 'Investment Required',
    safety_improvement: 'Safety Improvement',
    quality_improvement: 'Quality Improvement',
    delivery_improvement: 'Delivery Improvement',
    cost_reduction: 'Cost Reduction',
    morale_improvement: 'Morale Improvement'
  },

  PRIORITY_OPTIONS: [
    { value: 'high', label: 'High', color: 'text-red-600 bg-red-100' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600 bg-yellow-100' },
    { value: 'low', label: 'Low', color: 'text-green-600 bg-green-100' }
  ],

  STATUS_OPTIONS: [
    { value: 'assigned', label: 'Assigned', color: 'text-blue-600 bg-blue-100' },
    { value: 'in_progress', label: 'In Progress', color: 'text-yellow-600 bg-yellow-100' },
    { value: 'completed', label: 'Completed', color: 'text-green-600 bg-green-100' },
    { value: 'overdue', label: 'Overdue', color: 'text-red-600 bg-red-100' },
    { value: 'cancelled', label: 'Cancelled', color: 'text-gray-600 bg-gray-100' }
  ]
};
